import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { getPlatformSettings } from '../../services/api';
import * as supervisorApi from '../../services/supervisorApi';
import { useTherapistUiContextRegistration } from '../../context/TherapistUiContext'; // Help / Report-an-issue context
import axios from 'axios';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';

// New UI Components
import TopControlBar from './conversation/TopControlBar';
import ConversationSidebar from './conversation/ConversationSidebar';
import MessageArea from './conversation/MessageArea';
import AISuggestionOverlay from './conversation/AISuggestionOverlay';
import InputBar from './conversation/InputBar';
import UnifiedSettingsModal from './conversation/UnifiedSettingsModal';
import SummaryPanel from './conversation/SummaryPanel';
import ReviewPanel from './conversation/ReviewPanel';
import ReasoningTimeline from './reasoning/ReasoningTimeline';
import FormulationPanel from './reasoning/FormulationPanel';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TimelineIcon from '@mui/icons-material/Timeline';
import DescriptionIcon from '@mui/icons-material/Description';

// Existing components we still need
import ConversationListDisplay from './ConversationListDisplay';
import GoalManagementSystem from './GoalManagementSystem';
import AiGoalProposalCard from './AiGoalProposalCard';
import AiModeChangeSuggestionDialog from './AiModeChangeSuggestionDialog';
import AssessmentManager from './AssessmentManager';
import DelaySettingsModal from './DelaySettingsModal';
import ConversationSocketManager from './ConversationSocketManager';
import ConversationAutoplay from './ConversationAutoplay';
import AudioExportDialog from './AudioExportDialog';
import MobileNavigation from './conversation/MobileNavigation';

// Custom hooks
import { useConversationState } from '../../hooks/useConversationState';
import { useSummaryManagement } from '../../hooks/useSummaryManagement';
import useModeManagement from '../../hooks/useModeManagement';
import useSuggestionState from '../../hooks/useSuggestionState';
import useModalStates from '../../hooks/useModalStates';
import useAnnotationManagement from '../../hooks/useAnnotationManagement';
import useMessageHandling from '../../hooks/useMessageHandling';
import useResponsive from '../../hooks/useResponsive';

// Styles
import conversationUIStyles from './styles/conversationUIStyles';
import { getResponsiveStyles, mergeResponsiveStyles } from './styles/responsiveConversationStyles';

// Helper function to format timestamp
const formatMessageTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        return `${day}/${month}/${year}, ${time}`;
    }
};

function ConversationView({ token, clientId, clientName, onBack, userRole, socket, onNavigateToCareNotes, user, initialConversationId, initialHighlightedMessageId = null, gen2ApiBase = 'therapist' }) {
    const navigate = useNavigate();

    console.log('[ConversationView] Rendered with initialConversationId:', initialConversationId);

    // Responsive design hook
    const responsive = useResponsive();

    // Custom hooks for state management
    const conversationState = useConversationState(clientId, token);
    const summaryState = useSummaryManagement(conversationState.selectedConversation?._id, token);
    const modeState = useModeManagement();
    const suggestionState = useSuggestionState();
    const modalState = useModalStates();
    const annotationState = useAnnotationManagement(conversationState, token);
    const messageState = useMessageHandling(conversationState, token, modeState.currentMode);

    // Publish the selected conversation + control mode for support tickets
    // raised from the TopBar while this view is open.
    useTherapistUiContextRegistration('conversation', {
        conversationId: conversationState.selectedConversation?._id || undefined,
        controlMode: conversationState.selectedConversation ? modeState.currentMode : undefined,
    });

    // UI State
    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        localStorage.getItem('conversationSidebarCollapsed') === 'true'
    );
    const [showSummaryPanel, setShowSummaryPanel] = useState(false);
    const [showReviewPanel, setShowReviewPanel] = useState(false);
    // Gen 2 observability surfaces (Spec F / Reasoning Timeline + Formulation panel)
    const [showReasoningTimeline, setShowReasoningTimeline] = useState(false);
    const [showFormulationPanel, setShowFormulationPanel] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showExportDialog, setShowExportDialog] = useState(false);
    
    // Mobile-specific state
    const [mobileActiveTab, setMobileActiveTab] = useState('messages');
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    
    // Additional state
    const [success, setSuccess] = useState('');
    const [careNoteAlert, setCareNoteAlert] = useState(null);
    const [careNoteCount, setCareNoteCount] = useState(0);
    const [consultationsCount, setConsultationsCount] = useState(0);
    const [platformSettings, setPlatformSettings] = useState(null);
    const [showAutoplay, setShowAutoplay] = useState(false);
    // Seeded from initialHighlightedMessageId so an inbox notice can open the
    // transcript at the exact turn it was raised on. Until now this could only
    // be set by an internal click, so nothing outside the component could say
    // "show them this message".
    const [highlightedMessageId, setHighlightedMessageId] = useState(initialHighlightedMessageId || null);
    const [therapistVoiceSettings, setTherapistVoiceSettings] = useState({});
    const [aiStatus, setAiStatus] = useState('Ready');
    const [isProcessing, setIsProcessing] = useState(false);
    const [wasAiSuggestionRejected, setWasAiSuggestionRejected] = useState(false);
    // The draft the therapist discarded, sent with their own message so it is recorded.
    const discardedSuggestionRef = useRef(null);
    
    // Review state
    const [reviewResult, setReviewResult] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [reviewError, setReviewError] = useState('');
    
    // Refs
    const autoForwardSystemRef = useRef();
    const goalManagementSystemRef = useRef();
    
    // Merge desktop and responsive styles
    const responsiveStyles = getResponsiveStyles(responsive);
    const styles = mergeResponsiveStyles(conversationUIStyles, responsiveStyles);
    const messageInputRef = useRef();

    // Load care note count
    useEffect(() => {
        const loadCareNoteCount = async () => {
            if (!clientId || !token) return;
            try {
                const response = await api.getClientCareNotes(clientId, false, token);
                setCareNoteCount(response.careNotes?.length || 0);
            } catch (error) {
                console.error('Error loading care note count:', error);
            }
        };
        loadCareNoteCount();
    }, [clientId, token]);

    // Load consultations count
    useEffect(() => {
        const loadConsultationsCount = async () => {
            if (!clientId || !token) return;
            try {
                const response = await supervisorApi.getConsultationHistory({ clientId }, token);
                setConsultationsCount(response.consultations?.length || 0);
            } catch (error) {
                console.error('Error loading consultations count:', error);
            }
        };
        loadConsultationsCount();
    }, [clientId, token]);

    // Select conversation if initialConversationId is provided
    useEffect(() => {
        const conversations = conversationState.conversations || [];
        console.log('[ConversationView useEffect] Triggered with:', {
            initialConversationId,
            conversationsLength: conversations.length,
            conversationsRaw: conversationState.conversations,
            selectedConversationId: conversationState.selectedConversation?._id,
            loading: conversationState.loading
        });

        // Wait for conversations to finish loading
        if (conversationState.loading) {
            console.log('[ConversationView useEffect] Still loading conversations, waiting...');
            return;
        }

        if (initialConversationId && conversations.length > 0) {
            // Only select if it's different from the currently selected conversation
            if (!conversationState.selectedConversation || conversationState.selectedConversation._id !== initialConversationId) {
                const conversationToSelect = conversations.find(c => c._id === initialConversationId);
                console.log('[ConversationView useEffect] Found conversation to select:', conversationToSelect?._id);
                if (conversationToSelect) {
                    console.log('[ConversationView] Selecting conversation from initialConversationId:', initialConversationId);
                    conversationState.setSelectedConversation(conversationToSelect);
                } else {
                    console.log('[ConversationView useEffect] Conversation with ID not found in list:', {
                        lookingFor: initialConversationId,
                        availableIds: conversations.map(c => c._id)
                    });
                }
            } else {
                console.log('[ConversationView useEffect] Conversation already selected:', conversationState.selectedConversation._id);
            }
        } else {
            console.log('[ConversationView useEffect] Conditions not met:', {
                hasInitialConversationId: !!initialConversationId,
                hasConversations: conversations.length > 0,
                conversationsIsArray: Array.isArray(conversationState.conversations),
                loading: conversationState.loading
            });
        }
    }, [initialConversationId, conversationState.conversations, conversationState.selectedConversation, conversationState.setSelectedConversation, conversationState.loading]);

    // Fetch therapist voice settings
    useEffect(() => {
        const fetchTherapistVoiceSettings = async () => {
            if (!token || !clientId) return;
            
            try {
                const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const [therapistResponse, clientResponse] = await Promise.all([
                    axios.get(`${API_BASE_URL}/therapist/voice-settings`, {
                        headers: { 'x-auth-token': token }
                    }),
                    axios.get(`${API_BASE_URL}/therapist/client/${clientId}/voice-settings`, {
                        headers: { 'x-auth-token': token }
                    })
                ]);
                
                const combinedSettings = {
                    voiceSettings: therapistResponse.data?.voiceSettings || {},
                    clientVoiceOverrides: {
                        [clientId]: clientResponse.data?.voiceSettings || {}
                    },
                    elevenLabsVoiceId: therapistResponse.data?.voiceSettings?.legacyAIVoiceId || 
                                      therapistResponse.data?.voiceSettings?.defaultAITherapistVoice
                };
                
                setTherapistVoiceSettings(combinedSettings);
            } catch (error) {
                console.error('Error fetching therapist voice settings:', error);
            }
        };
        
        if (token && clientId) {
            fetchTherapistVoiceSettings();
        }
    }, [token, clientId]);

    // Initialize conversation mode when a conversation is SELECTED (or switched).
    //
    // CRITICAL: depend on the conversation _id, not on the whole object
    // reference. handleNewMessage spreads the conversation on every incoming
    // message (`{ ...prevConvo, messages: [...] }` in useMessageHandling),
    // so the object reference changes on every patient/therapist/system
    // message. If we depend on the whole reference, this effect re-runs on
    // every message and calls `suggestionState.clearAllSuggestions()` —
    // wiping any AI suggestion the moment a client reply lands, which is
    // exactly the "AI stopped" stall behaviour the user reported in
    // Assisted mode.
    useEffect(() => {
        if (conversationState.selectedConversation) {
            const conv = conversationState.selectedConversation;
            modeState.resetModeState(conv.controlMode || 'ai');
            suggestionState.clearAllSuggestions();
            // Recover any persisted assisted-mode suggestion that was emitted
            // while the tab was unmounted / socket was reconnecting / etc.
            // (Server clears pendingAiSuggestion on send, discard equivalent,
            //  and mode change — so anything present here is genuinely pending.)
            const pending = conv.pendingAiSuggestion;
            if (pending?.text && conv.controlMode === 'assisted' && pending.mode === 'assisted') {
                suggestionState.setCurrentSuggestion(pending.text);
            }
            messageState.setAutoForwardEnabled(conv.autoForwardEnabled || false);
            messageState.userIsNearBottomRef.current = true;

            setTimeout(() => {
                messageInputRef.current?.focus();
            }, 100);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationState.selectedConversation?._id]);

    // Load conversations on mount
    useEffect(() => {
        conversationState.fetchConversations();
    }, []);
    
    // Load platform settings
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const settings = await getPlatformSettings(token);
                setPlatformSettings(settings);
            } catch (error) {
                console.error('Failed to load platform settings:', error);
            }
        };
        
        if (token) {
            fetchSettings();
        }
    }, [token]);

    // Save sidebar collapsed state
    const handleToggleSidebar = useCallback(() => {
        if (responsive.shouldShowDrawerSidebar) {
            // On mobile/tablet, toggle drawer
            setMobileSidebarOpen(!mobileSidebarOpen);
        } else {
            // On desktop, toggle collapse
            const newState = !sidebarCollapsed;
            setSidebarCollapsed(newState);
            localStorage.setItem('conversationSidebarCollapsed', newState.toString());
        }
    }, [sidebarCollapsed, mobileSidebarOpen, responsive.shouldShowDrawerSidebar]);
    
    // Define handleViewCareNotes before using it
    const handleViewCareNotes = useCallback(() => {
        if (onNavigateToCareNotes) {
            onNavigateToCareNotes();
        } else {
            navigate(`/therapist/client/${clientId}?tab=7`);
        }
    }, [navigate, clientId, onNavigateToCareNotes]);
    
    // Mobile navigation handler
    const handleMobileTabChange = useCallback((tab) => {
        setMobileActiveTab(tab);
        
        switch (tab) {
            case 'messages':
                setMobileSidebarOpen(false);
                break;
            case 'info':
                setMobileSidebarOpen(true);
                break;
            case 'notes':
                handleViewCareNotes();
                break;
            case 'actions':
                // Show actions menu - could be a bottom sheet or menu
                setShowSettingsModal(true);
                break;
            default:
                break;
        }
    }, [handleViewCareNotes]);

    // Handle conversation selection
    const handleSelectConversation = useCallback((conversation) => {
        conversationState.setSelectedConversation(conversation);
        modeState.resetModeState(conversation.controlMode || 'ai');
        suggestionState.clearAllSuggestions();
        modalState.closeAllModals();
        
        suggestionState.setTherapistInput('');
        suggestionState.setEditingState('isEditingManual', false);
        suggestionState.setEditingState('editText', '');
        
        setReviewResult(null);
        setReviewError('');
        setReviewLoading(false);
    }, [conversationState, modeState, suggestionState, modalState]);

    // Handle mode changes
    const handleSetMode = useCallback(async (newMode) => {
        if (!conversationState.selectedConversation) return;
        
        setIsProcessing(true);
        setAiStatus('Switching mode...');
        
        try {
            const response = await modeState.handleSetMode(
                newMode, 
                conversationState.selectedConversation._id, 
                token
            );
            
            conversationState.setSelectedConversation(prev => ({
                ...prev,
                controlMode: response.mode,
                status: response.status
            }));

            if (newMode !== 'assisted') {
                suggestionState.clearAllSuggestions();
            }
            
            setSuccess(`Mode changed to ${newMode}`);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Error setting mode:', err);
        } finally {
            setIsProcessing(false);
            setAiStatus('Ready');
        }
    }, [conversationState, modeState, suggestionState, token]);

    // Socket event handlers
    const handleAiSuggestion = useCallback((data) => {
        if (data.suggestion && data.suggestion.trim()) {
            setAiStatus('New suggestion received');
            suggestionState.setCurrentSuggestion(data.suggestion);
            suggestionState.setEditingState('isEditingManual', false);
            suggestionState.setEditingState('editText', '');
            setTimeout(() => setAiStatus('Ready'), 2000);
        }
    }, [suggestionState]);

    const handleNewMessage = useCallback((data) => {
        messageState.handleNewMessage(data);
    }, [messageState]);

    const handleModeChange = useCallback((data) => {
        modeState.updateMode(data.newMode);
        messageState.handleModeChange(data);
    }, [modeState, messageState]);

    const handleModeChangeSuggestion = useCallback((data) => {
        suggestionState.setModeChangeSuggestion({
            targetMode: data.targetMode,
            reason: data.reason,
            detectedContext: data.detectedContext,
            urgency: data.urgency,
            timestamp: data.timestamp
        });
        modalState.openModal('modeChangeSuggestion');
    }, [suggestionState, modalState]);

    const handleAiGoalProposal = useCallback((data) => {
        suggestionState.triggerGoalProposalUpdate();
        suggestionState.setLocalPendingGoalProposal(data);
    }, [suggestionState]);

    const handleAiAssessmentProposal = useCallback((data) => {
        suggestionState.setAssessmentSuggestion(data);
    }, [suggestionState]);

    const handleClientGoalActionUpdate = useCallback((data) => {
        suggestionState.triggerGoalProposalUpdate();
        messageState.handleClientGoalActionUpdate(data);
    }, [suggestionState, messageState]);

    const handleCareNoteAlert = useCallback((alert) => {
        setCareNoteAlert(alert);
        setSuccess(`Care Note Alert: ${alert.concern.concernType} - ${alert.clientName}`);
        setTimeout(() => {
            setCareNoteAlert(null);
            setSuccess('');
        }, 10000);
    }, []);

    const handleCreateCareNoteFromMessage = useCallback((messageText, messageId) => {
        localStorage.setItem('careNoteInitialContent', messageText);
        window.location.href = `/therapist/dashboard#careNotes`;
        setSuccess('Navigate to Care Notes tab to create the note');
    }, []);

    // Message sending handler
    const handleTherapistSendMessage = useCallback(async (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        
        const messageText = suggestionState.suggestionState.therapistInput;
        if (!messageText.trim()) return;

        try {
            await messageState.handleTherapistSendMessage(messageText, wasAiSuggestionRejected, discardedSuggestionRef.current);
            discardedSuggestionRef.current = null;
            suggestionState.setTherapistInput('');
            setWasAiSuggestionRejected(false); // Reset after sending
        } catch (err) {
            // Error handling is done in the hook
        }
    }, [suggestionState, messageState, wasAiSuggestionRejected]);

    // Auto-forward toggle
    const handleToggleAutoForward = useCallback(async (enabled) => {
        try {
            const message = await messageState.handleToggleAutoForward(enabled);
            setSuccess(message);
        } catch (err) {
            messageState.setSendError(err.message || 'Failed to toggle auto-forward');
        }
    }, [messageState]);

    // Request review
    const handleRequestReview = useCallback(async (conversationId, reviewerPersonaId = null) => {
        setReviewLoading(true);
        setReviewError('');
        setReviewResult(null);

        try {
            const result = await messageState.handleRequestReview(conversationId, reviewerPersonaId);
            setReviewResult(result);
            setSuccess('Review completed successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setReviewError(err.message || 'Failed to request review');
        } finally {
            setReviewLoading(false);
        }
    }, [messageState]);

    // Generate summary
    const handleGenerateSummary = useCallback(async () => {
        await summaryState.handleGenerateSummary();
    }, [summaryState]);

    // Navigation handlers
    const handleConsultSupervisor = useCallback(() => {
        if (conversationState.selectedConversation) {
            navigate(`/supervisor/consultation/new/${clientId}/${conversationState.selectedConversation._id}`);
        }
    }, [navigate, clientId, conversationState.selectedConversation]);

    // Placeholder text for input
    const getPlaceholderText = useCallback(() => {
        if (goalManagementSystemRef?.current?.isGoalModalOpen()) return "Proposing goal in modal above...";
        if (modalState.isModalOpen('manualAssessment')) return "Initiating assessment in modal above...";
        if (modeState.currentMode === 'ai') {
            return "Switch to Human or Assisted mode to send messages";
        }
        if (modeState.currentMode === 'assisted') {
            if (suggestionState.suggestionState.isEditingManual) {
                return "Editing suggestion above...";
            }
            if ((suggestionState.suggestionState.current || goalManagementSystemRef?.current?.hasPendingGoalProposal()) && 
                !suggestionState.suggestionState.isEditingManual && 
                !suggestionState.suggestionState.isEditing) {
                return "Review and send/edit the AI suggestion above";
            }
            return "Type a message for the client (with AI assistance)";
        }
        return "Type a message for the client";
    }, [modeState.currentMode, suggestionState.suggestionState, modalState]);

    // Clear success messages
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    // Render loading state
    if (conversationState.loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>Loading conversations...</Typography>
            </Box>
        );
    }

    // Render error state
    if (conversationState.error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {conversationState.error}
                </Alert>
                <Typography variant="body1">
                    Please try refreshing the page or contact support if the issue persists.
                </Typography>
            </Box>
        );
    }

    // Render conversation list if no conversation selected
    if (!conversationState.selectedConversation) {
        return (
            <Box sx={styles.mainContainer}>
                <ConversationListDisplay
                    conversations={conversationState.conversations}
                    selectedConversationId={conversationState.selectedConversation?._id}
                    onSelectConversation={handleSelectConversation}
                    token={token}
                    onConversationsUpdate={conversationState.fetchConversations}
                />
            </Box>
        );
    }

    // Main conversation interface
    return (
        <Box sx={styles.mainContainer}>
            {/* Socket Event Manager */}
            <ConversationSocketManager
                socket={socket}
                selectedConversation={conversationState.selectedConversation}
                onAiSuggestion={handleAiSuggestion}
                onNewMessage={handleNewMessage}
                onModeChange={handleModeChange}
                onModeChangeSuggestion={handleModeChangeSuggestion}
                onAiGoalProposal={handleAiGoalProposal}
                onAiAssessmentProposal={handleAiAssessmentProposal}
                onClientGoalActionUpdate={handleClientGoalActionUpdate}
                onCareNoteAlert={handleCareNoteAlert}
                onSocketConnected={() => {}}
            />

            {/* Top Control Bar */}
            <TopControlBar
                currentMode={modeState.currentMode}
                onModeChange={handleSetMode}
                clientName={clientName}
                clientId={clientId}
                conversationId={conversationState.selectedConversation?._id}
                conversationStatus={conversationState.selectedConversation?.status}
                autoForwardEnabled={messageState.autoForwardEnabled}
                careNoteCount={careNoteCount}
                consultationsCount={consultationsCount}
                onConsultSupervisor={handleConsultSupervisor}
                onToggleAutoplay={() => setShowAutoplay(!showAutoplay)}
                onExportAudio={() => setShowExportDialog(true)}
                onOpenSettings={() => setShowSettingsModal(true)}
                onCloseConversation={() => {
                    conversationState.setSelectedConversation(null);
                    suggestionState.clearAllSuggestions();
                    modalState.closeAllModals();
                    setReviewResult(null);
                    setReviewError('');
                    setReviewLoading(false);
                }}
                onNavigateToCareNotes={handleViewCareNotes}
                isLoading={modeState.modeChangeLoading || isProcessing}
                styles={styles}
                responsive={responsive}
                onToggleSidebar={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            />

            {/* Content Area */}
            <Box sx={styles.contentArea}>
                {/* Mobile Backdrop for drawer */}
                {responsive.shouldShowDrawerSidebar && (
                    <Box
                        sx={{
                            ...styles.mobileBackdrop,
                            ...(mobileSidebarOpen && styles.mobileBackdropVisible)
                        }}
                        onClick={() => setMobileSidebarOpen(false)}
                    />
                )}
                
                {/* Sidebar */}
                <ConversationSidebar
                    isCollapsed={sidebarCollapsed}
                    isOpen={mobileSidebarOpen}
                    onToggleCollapse={handleToggleSidebar}
                    conversation={conversationState.selectedConversation}
                    clientInfo={{ name: clientName, id: clientId }}
                    currentMode={modeState.currentMode}
                    autoForwardEnabled={messageState.autoForwardEnabled}
                    onToggleAutoForward={handleToggleAutoForward}
                    onGenerateSummary={() => {
                        summaryState.handleGenerateSummary();
                        setShowSummaryPanel(true);
                    }}
                    onViewSummary={() => setShowSummaryPanel(true)}
                    hasSummary={summaryState.summariesLoaded}
                    onRequestReview={() => setShowReviewPanel(true)}
                    onExportTranscript={() => {/* Implement export */}}
                    onViewCareNotes={handleViewCareNotes}
                    onProposeGoal={() => goalManagementSystemRef?.current?.openManualGoalModal()}
                    onInitiateAssessment={() => modalState.openModal('manualAssessment')}
                    onOpenSettings={() => setShowSettingsModal(true)}
                    onOpenDelaySettings={() => modalState.openModal('delaySettings')}
                    aiStatus={aiStatus}
                    isProcessing={isProcessing}
                    careNoteCount={careNoteCount}
                    styles={styles}
                    responsive={responsive}
                    isMobileOpen={mobileSidebarOpen}
                    onMobileClose={() => setMobileSidebarOpen(false)}
                />

                {/* Message Area */}
                <Box sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    position: 'relative',
                    minHeight: 0, // Important for flex children with overflow
                    overflow: 'hidden' // Prevent overflow on parent
                }}>
                    {/* Goal Management System */}
                    <GoalManagementSystem
                        ref={goalManagementSystemRef}
                        selectedConversation={conversationState.selectedConversation}
                        socket={socket}
                        token={token}
                        currentMode={modeState.currentMode}
                        userRole={userRole}
                        currentSuggestion={suggestionState.suggestionState.current}
                        goalProposalTrigger={suggestionState.suggestionState.goalProposalTrigger}
                        localPendingGoalProposal={suggestionState.suggestionState.localPendingGoalProposal}
                        onUpdateLocalPendingGoalProposal={suggestionState.setLocalPendingGoalProposal}
                        onCanProposeManualGoalChange={() => {}}
                    />

                    {/* Gen 2 reasoning observability toggles. Hidden if neither
                        the timeline nor the formulation can be loaded (the
                        components handle their own empty states gracefully). */}
                    {conversationState.selectedConversation && clientId && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, px: 1, pt: 0.5 }}>
                            <Tooltip title="Reasoning Timeline — Gen 2 events for this session">
                                <IconButton size="small" onClick={() => setShowReasoningTimeline((v) => !v)} sx={{ color: showReasoningTimeline ? 'primary.main' : 'text.secondary' }}>
                                    <TimelineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Clinical Formulation — current state + version history">
                                <IconButton size="small" onClick={() => setShowFormulationPanel((v) => !v)} sx={{ color: showFormulationPanel ? 'primary.main' : 'text.secondary' }}>
                                    <DescriptionIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {/* Messages */}
                    <MessageArea
                        messages={conversationState.selectedConversation.messages || []}
                        userRole={userRole}
                        currentMode={modeState.currentMode}
                        onAnnotateMessage={annotationState.handleStartAnnotation}
                        onCreateCareNote={handleCreateCareNoteFromMessage}
                        messageContainerRef={messageState.messageContainerRef}
                        userIsNearBottomRef={messageState.userIsNearBottomRef}
                        onScroll={messageState.handleScroll}
                        platformSettings={platformSettings}
                        therapistSettings={therapistVoiceSettings}
                        clientId={clientId}
                        highlightedMessageId={highlightedMessageId}
                        token={token}
                        conversationId={conversationState.selectedConversation?._id}
                        annotationState={annotationState}
                        formatTimestamp={formatMessageTimestamp}
                        styles={styles}
                        responsive={responsive}
                        gen2ApiBase={gen2ApiBase}
                    />

                    {/* AI Suggestion Overlay */}
                    <AISuggestionOverlay
                        suggestion={suggestionState.suggestionState.current}
                        isEditing={suggestionState.suggestionState.isEditingManual}
                        editedText={suggestionState.suggestionState.editText}
                        onEditChange={(e) => suggestionState.setEditingState('editText', e.target.value)}
                        onSendAsIs={async () => {
                            // Guard: prevent double-fire while the request is
                            // in flight. Without this, a slow /send can leave
                            // the button enabled long enough for the user to
                            // click it again, sending duplicates.
                            if (messageState.isSending) return;
                            messageState.setIsSending(true);
                            try {
                                const messageData = {
                                    message: suggestionState.suggestionState.current,
                                    conversationId: conversationState.selectedConversation._id,
                                    senderRole: 'human_therapist',
                                    sentInMode: 'assisted',
                                    aiSuggestionOriginalText: suggestionState.suggestionState.current,
                                    aiSuggestionHandling: 'SENT_AS_IS'
                                };
                                await api.sendMessage(messageData, token);
                                suggestionState.clearTextSuggestion();
                                setWasAiSuggestionRejected(false); // Reset rejection flag
                                setSuccess('Suggestion sent successfully!');
                            } catch (err) {
                                messageState.setSendError(err.message || 'Failed to send suggestion');
                            } finally {
                                messageState.setIsSending(false);
                            }
                        }}
                        onStartEdit={() => {
                            suggestionState.setEditingState('isEditingManual', true);
                            suggestionState.setEditingState('editText', suggestionState.suggestionState.current);
                        }}
                        onCancelEdit={() => {
                            suggestionState.cancelEditingSuggestion();
                        }}
                        onSendEdited={async () => {
                            if (!suggestionState.suggestionState.editText?.trim()) {
                                messageState.setSendError('Cannot send empty message');
                                return;
                            }
                            if (messageState.isSending) return;
                            messageState.setIsSending(true);
                            try {
                                const messageData = {
                                    message: suggestionState.suggestionState.editText,
                                    conversationId: conversationState.selectedConversation._id,
                                    senderRole: 'human_therapist',
                                    sentInMode: 'assisted',
                                    aiSuggestionOriginalText: suggestionState.suggestionState.current,
                                    aiSuggestionHandling: 'SENT_EDITED'
                                };
                                await api.sendMessage(messageData, token);
                                suggestionState.clearTextSuggestion();
                                setWasAiSuggestionRejected(false); // Reset rejection flag
                                setSuccess('Edited suggestion sent successfully!');
                            } catch (err) {
                                messageState.setSendError(err.message || 'Failed to send edited suggestion');
                            } finally {
                                messageState.setIsSending(false);
                            }
                        }}
                        onDiscard={() => {
                            discardedSuggestionRef.current = suggestionState.suggestionState.current || null;
                            suggestionState.clearTextSuggestion();
                            setWasAiSuggestionRejected(true); // Mark that suggestion was rejected
                            setTimeout(() => {
                                messageInputRef.current?.focus();
                            }, 100);
                        }}
                        isSending={messageState.isSending}
                        show={!!suggestionState.suggestionState.current && modeState.currentMode === 'assisted'}
                        styles={styles}
                        responsive={responsive}
                    />

                    {/* AI Goal Proposal Card */}
                    {suggestionState.suggestionState.localPendingGoalProposal && (
                        <AiGoalProposalCard
                            proposal={suggestionState.suggestionState.localPendingGoalProposal}
                            conversationId={conversationState.selectedConversation._id}
                            token={token}
                            onProposalHandled={suggestionState.setLocalPendingGoalProposal}
                        />
                    )}

                    {/* Assessment Manager */}
                    <AssessmentManager
                        conversationState={conversationState}
                        token={token}
                        suggestionState={suggestionState}
                        modalState={modalState}
                        clientName={clientName}
                        onSuccess={setSuccess}
                        onError={(error) => console.error('Assessment error:', error)}
                    />

                    {/* Input Bar */}
                    {userRole === 'therapist' && (
                        <InputBar
                            ref={messageInputRef}
                            currentMode={modeState.currentMode}
                            therapistInputText={suggestionState.suggestionState.therapistInput}
                            onInputChange={(e) => suggestionState.setTherapistInput(e.target.value)}
                            onSendMessage={handleTherapistSendMessage}
                            isSending={messageState.isSending}
                            sendError={messageState.sendError}
                            canProposeManualGoal={true}
                            onOpenManualGoalModal={() => goalManagementSystemRef?.current?.openManualGoalModal()}
                            canInitiateAssessment={true}
                            onOpenAssessmentModal={() => modalState.openModal('manualAssessment')}
                            isDisabled={
                                modeState.modeChangeLoading || 
                                annotationState.annotationLoading ||
                                (modeState.currentMode === 'assisted' && suggestionState.suggestionState.isEditingManual) ||
                                modalState.isModalOpen('manualAssessment')
                            }
                            placeholder={getPlaceholderText()}
                            styles={styles}
                            responsive={responsive}
                        />
                    )}
                </Box>
            </Box>

            {/* Status Messages */}
            {(success || modeState.modeChangeSuccess || modeState.modeChangeError) && (
                <Box sx={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
                    {success && (
                        <Alert severity="success" sx={{ mb: 1 }}>
                            {success}
                        </Alert>
                    )}
                    {modeState.modeChangeSuccess && (
                        <Alert severity="success" sx={{ mb: 1 }}>
                            {modeState.modeChangeSuccess}
                        </Alert>
                    )}
                    {modeState.modeChangeError && (
                        <Alert severity="error">
                            {modeState.modeChangeError}
                        </Alert>
                    )}
                </Box>
            )}

            {/* Modals and Panels */}
            <UnifiedSettingsModal
                open={showSettingsModal}
                onClose={() => setShowSettingsModal(false)}
                conversationId={conversationState.selectedConversation?._id}
                token={token}
                currentSettings={conversationState.selectedConversation}
                onSettingsUpdate={(updatedSettings) => {
                    conversationState.setSelectedConversation(prev => ({
                        ...prev,
                        ...updatedSettings
                    }));
                    setSuccess('Settings saved successfully!');
                }}
            />

            <SummaryPanel
                open={showSummaryPanel}
                onClose={() => setShowSummaryPanel(false)}
                therapistSummary={summaryState.therapistSummary}
                clientSummary={summaryState.clientSummary}
                reflectionPrompts={summaryState.reflectionPrompts}
                summariesLoaded={summaryState.summariesLoaded}
                summaryLoading={summaryState.summaryLoading}
                summaryError={summaryState.summaryError}
                onGenerateSummary={handleGenerateSummary}
                isEditingClientSummary={summaryState.isEditingClientSummary}
                editableClientSummaryText={summaryState.editableClientSummaryText}
                onStartEditClientSummary={summaryState.handleStartEditClientSummary}
                onCancelEditClientSummary={summaryState.handleCancelEditClientSummary}
                onSaveEditedClientSummary={summaryState.handleSaveEditedClientSummary}
                onEditableClientSummaryTextChange={summaryState.handleEditableClientSummaryTextChange}
                clientSummaryEditError={summaryState.clientSummaryEditError}
                clientSummaryEditLoading={summaryState.clientSummaryEditLoading}
            />

            <ReviewPanel
                open={showReviewPanel}
                onClose={() => setShowReviewPanel(false)}
                conversationId={conversationState.selectedConversation?._id}
                onRequestReview={handleRequestReview}
                reviewResult={reviewResult}
                reviewLoading={reviewLoading}
                reviewError={reviewError}
                token={token}
            />

            <DelaySettingsModal
                open={modalState.isModalOpen('delaySettings')}
                onClose={() => modalState.closeModal('delaySettings')}
                conversationId={conversationState.selectedConversation?._id}
                token={token}
                clientName={clientName}
                onSuccess={(updatedData) => {
                    conversationState.setSelectedConversation(prev => ({
                        ...prev,
                        aiResponseDelaySettings: updatedData.aiResponseDelaySettings
                    }));
                    setSuccess('AI response delay settings saved successfully!');
                }}
            />

            <AiModeChangeSuggestionDialog
                open={modalState.isModalOpen('modeChangeSuggestion')}
                onClose={() => modalState.closeModal('modeChangeSuggestion')}
                suggestion={suggestionState.suggestionState.modeChange}
                token={token}
                conversationId={conversationState.selectedConversation?._id}
                onSuccess={(action, newMode) => {
                    if (action === 'approve') {
                        modeState.updateMode(newMode);
                        setSuccess(`Mode changed to ${newMode} by approving AI suggestion`);
                    }
                }}
            />

            {showAutoplay && (
                <ConversationAutoplay
                    messages={conversationState.selectedConversation.messages || []}
                    userSettings={user}
                    platformSettings={platformSettings}
                    therapistSettings={therapistVoiceSettings}
                    clientId={clientId}
                    conversationId={conversationState.selectedConversation._id}
                    token={token}
                    onMessageHighlight={(messageId) => {
                        setHighlightedMessageId(messageId);
                        const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
                        if (messageElement) {
                            messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }}
                    onClose={() => setShowAutoplay(false)}
                />
            )}

            {showExportDialog && (
                <AudioExportDialog
                    open={showExportDialog}
                    onClose={() => setShowExportDialog(false)}
                    conversationId={conversationState.selectedConversation?._id}
                    messages={conversationState.selectedConversation?.messages || []}
                />
            )}
            
            {/* Mobile Navigation */}
            {responsive.shouldShowMobileLayout && (
                <MobileNavigation
                    activeTab={mobileActiveTab}
                    onTabChange={handleMobileTabChange}
                    careNoteCount={careNoteCount}
                    hasNewMessages={false}
                    styles={styles}
                />
            )}

            {/* Gen 2 Reasoning Timeline + Formulation slide-out panels.
                Temporary (overlay) drawers with backdrop click-to-close,
                ESC-to-close, and a sticky header close button so the user
                always has multiple ways out. */}
            <ReasoningTimeline
                open={showReasoningTimeline}
                onClose={() => setShowReasoningTimeline(false)}
                conversationId={conversationState.selectedConversation?._id}
                socket={socket}
                token={token}
                gen2ApiBase={gen2ApiBase}
                onJumpToEventTime={null /* future: scroll-to-message-by-time */}
            />
            <FormulationPanel
                open={showFormulationPanel}
                onClose={() => setShowFormulationPanel(false)}
                clientId={clientId}
                token={token}
                gen2ApiBase={gen2ApiBase}
            />
        </Box>
    );
}

export default ConversationView;